import { Box, Button, GlobalStyles, Paper, Stack, Typography } from '@mui/material'
import { QRCodeSVG } from 'qrcode.react'
import type { ReactElement } from 'react'
import { useCallback } from 'react'
import { Helmet } from 'react-helmet'
import { IoBatteryFullOutline, IoCashOutline, IoNotificationsOutline, IoPeopleOutline, IoPrintOutline, IoWifiOutline } from 'react-icons/io5'
import { baseURL, deviceCampaignLabel, deviceCampaignPriceNumber, devicePriceNumber, serviceName, supportEmail } from 'src/config'

const TINT = '#FFF6E5'

// 展示会のビラ用。A4 1枚で完結する紹介ページ (noindex)
export const Flyer = () => {
  const handlePrint = useCallback(() => {
    window.print()
  }, [])

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <meta name="robots" content="noindex, nofollow" />
        <title>{serviceName} | 展示会リーフレット</title>
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      </Helmet>

      {/* 印刷 / PDF出力時のスタイル。ヘッダー・フッター・操作ボタンを隠しA4 1枚に最適化する */}
      <GlobalStyles
        styles={{
          '@media print': {
            '*': { WebkitPrintColorAdjust: 'exact', printColorAdjust: 'exact' },
            'header, footer, .no-print': { display: 'none !important' },
            // アプリのレイアウトラッパー(main配下)の余白を全て打ち消す。
            // 特に content Box の pb:12(96px)/mt:9 が残ると297mmシートが2ページ目に溢れる
            'main, main > div, main > div > div': { margin: '0 !important', padding: '0 !important' },
            body: { backgroundColor: '#fff' },
            img: { maxWidth: '100% !important' },
            '.flyer-root': { maxWidth: 'none !important', margin: '0 !important', padding: '0 !important' },
            // A4ちょうど1枚のシートにする (@page margin:0 のため余白はpaddingで作る)。overflow:hiddenで2ページ目を防ぐ
            '.flyer-page': {
              boxSizing: 'border-box',
              width: '210mm',
              height: '297mm',
              margin: '0 auto !important',
              padding: '14mm 15mm 12mm',
              overflow: 'hidden',
              boxShadow: 'none !important',
              border: 'none !important',
              // 余白を全セクション間に均等分配し、footerを下端へ
              justifyContent: 'space-between',
            },
            '.flyer-page > * + *': { marginTop: '0 !important' },
            '.flyer-qr': { padding: '10px !important' },
            '.flyer-qr svg': { width: '84px !important', height: '84px !important', display: 'block' },
          },
          '@page': { size: 'A4', margin: 0 },
        }}
      />

      <Box className="flyer-root" sx={{ maxWidth: 820, mx: 'auto' }}>
        <Stack
          direction="row"
          className="no-print"
          sx={{
            justifyContent: 'flex-end',
            mb: 2,
          }}
        >
          <Button variant="contained" startIcon={<IoPrintOutline />} onClick={handlePrint} sx={{ borderRadius: 999, fontWeight: 700, boxShadow: 'none' }}>
            印刷 / PDF出力
          </Button>
        </Stack>

        <Stack spacing={2} className="flyer-page">
          <FlyerHeader />
          <Hero />
          <Benefits />
          <Notifications />
          <Management />
          <PriceAndCta />
        </Stack>
      </Box>
    </>
  )
}

const FlyerHeader = () => {
  return (
    <Stack
      direction="row"
      sx={{
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      <Stack
        direction="row"
        spacing={1.5}
        sx={{
          alignItems: 'center',
        }}
      >
        <Box component="img" src="/logomark-color.svg" alt={serviceName} sx={{ height: 38 }} />
        <Box>
          <Typography sx={{ fontWeight: 800, fontSize: 22, lineHeight: 1.1 }}>{serviceName}</Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            IoT罠とその管理サービス
          </Typography>
        </Box>
      </Stack>
      <Box sx={{ px: 1.5, py: 0.5, borderRadius: 999, backgroundColor: 'primary.main', color: '#fff', fontSize: 12, fontWeight: 700 }}>
        {deviceCampaignLabel}実施中
      </Box>
    </Stack>
  )
}

const Hero = () => {
  return (
    <Paper
      elevation={0}
      sx={{ borderRadius: 4, overflow: 'hidden', border: '1px solid', borderColor: 'divider', background: `linear-gradient(135deg, ${TINT} 0%, #FFFFFF 62%)` }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        sx={{
          alignItems: 'stretch',
        }}
      >
        <Box sx={{ flex: 1.3, p: { xs: 1.75, sm: 2 }, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <Typography variant="overline" sx={{ color: 'primary.dark', lineHeight: 1.2, fontSize: 11 }}>
            LTEモデル
          </Typography>
          <Typography component="h1" sx={{ fontWeight: 900, fontSize: { xs: 18, sm: 20 }, lineHeight: 1.2, mt: 0.25, letterSpacing: '-0.01em' }}>
            見回りの負担を、大幅に軽減。
          </Typography>
          <Typography sx={{ mt: 0.75, fontSize: 12, color: 'text.secondary', lineHeight: 1.6 }}>
            くくり罠・箱罠に取り付けるだけ。罠の作動をスマホやメールにお知らせ。親機・中継機は不要、単独で動作する低価格なIoT罠です。
          </Typography>
        </Box>
        <Box
          className="flyer-hero-media"
          sx={{
            flex: 1,
            width: '100%',
            minHeight: { xs: 240 },
            height: { sm: 320 },
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: (theme) => theme.palette.grey[300],
            p: 1.5,
          }}
        >
          <Box
            component="img"
            src="/wanapp_device_image.png"
            alt="LTEモデル"
            sx={{ flex: 1, minHeight: 0, maxWidth: '100%', objectFit: 'contain', display: 'block' }}
          />
          <Typography variant="caption" sx={{ mt: 0.5, textAlign: 'right', color: 'text.secondary' }}>
            ※画像はイメージです
          </Typography>
        </Box>
      </Stack>
    </Paper>
  )
}

type BenefitProps = { icon: ReactElement; title: string; body: string }
const BENEFITS: BenefitProps[] = [
  { icon: <IoCashOutline />, title: '通信費0円', body: '月額料金なし。約10年分の通信費を本体価格に含みます。' },
  { icon: <IoWifiOutline />, title: '親機・中継機 不要', body: 'LTE-M回線で単独動作。難しい設置や設定は不要です。' },
  { icon: <IoBatteryFullOutline />, title: '電池で長期稼働', body: '単3電池3本で約3〜4ヶ月。手軽に運用できます。' },
]

const Benefits = () => {
  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
      {BENEFITS.map((b) => (
        <Paper key={b.title} elevation={0} sx={{ flex: 1, p: 1.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
          <Stack
            direction="row"
            spacing={1}
            sx={{
              alignItems: 'center',
            }}
          >
            <Box sx={{ display: 'flex', fontSize: 24, color: 'primary.dark' }}>{b.icon}</Box>
            <Typography sx={{ fontWeight: 800, fontSize: 15.5 }}>{b.title}</Typography>
          </Stack>
          <Typography sx={{ fontSize: 12.5, lineHeight: 1.6, color: 'text.secondary', mt: 0.5 }}>{b.body}</Typography>
        </Paper>
      ))}
    </Stack>
  )
}

const Notifications = () => {
  return (
    <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', p: 1.5 }}>
      <Stack
        direction="row"
        spacing={1}
        sx={{
          alignItems: 'center',
          mb: 1,
        }}
      >
        <Box sx={{ display: 'flex', fontSize: 20, color: 'primary.dark' }}>
          <IoNotificationsOutline />
        </Box>
        <Typography sx={{ fontWeight: 800, fontSize: 16 }}>3つの通知でしっかり見守り</Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          メール / LINE / 管理画面に対応
        </Typography>
      </Stack>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
        <NoticeItem title="罠の作動 (捕獲)" body="罠が作動したことを検知し、いち早くお知らせ。" />
        <NoticeItem title="故障・電池切れ" body="不具合や電池切れの可能性を検知して通知。" />
        <NoticeItem title="電源ON" body="デバイスの起動を検知して通知。" />
      </Stack>
    </Paper>
  )
}

const NoticeItem = ({ title, body }: { title: string; body: string }) => {
  return (
    <Box sx={{ flex: 1, pl: 1.5, borderLeft: '4px solid', borderLeftColor: 'primary.main' }}>
      <Typography sx={{ fontWeight: 700, fontSize: 14 }}>{title}</Typography>
      <Typography sx={{ fontSize: 12.5, lineHeight: 1.6, color: 'text.secondary' }}>{body}</Typography>
    </Box>
  )
}

const Management = () => {
  return (
    <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', p: 1.5 }}>
      <Stack
        direction="row"
        spacing={1}
        sx={{
          alignItems: 'center',
          mb: 1,
        }}
      >
        <Box sx={{ display: 'flex', fontSize: 20, color: 'primary.dark' }}>
          <IoPeopleOutline />
        </Box>
        <Typography sx={{ fontWeight: 800, fontSize: 16 }}>管理Webアプリ</Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          無料でご利用可能
        </Typography>
      </Stack>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
        <NoticeItem title="グループで共有" body="個人から自治体・猟友会まで、メンバーでデバイスを共有管理。" />
        <NoticeItem title="Webで一元管理" body="デバイスを管理でき、一覧画面で全機の状態を一目で確認できます。" />
        <NoticeItem title="サブグループで出し分け" body="デバイスごとに通知先を分けて送れます。（有料プラン）" />
      </Stack>
    </Paper>
  )
}

const PriceAndCta = () => {
  return (
    <Paper
      className="flyer-foot"
      elevation={0}
      sx={{ borderRadius: 4, border: '1px solid', borderColor: 'divider', backgroundColor: TINT, p: { xs: 2, sm: 2.5 } }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={{ xs: 2, sm: 4 }}
        sx={{
          justifyContent: 'space-between',
          alignItems: { sm: 'center' },
        }}
      >
        {/* 価格 */}
        <Box sx={{ flex: 1 }}>
          <Box
            sx={{
              display: 'inline-block',
              px: 1.5,
              py: 0.5,
              mb: 1,
              borderRadius: 999,
              backgroundColor: 'primary.main',
              color: '#fff',
              fontSize: 12,
              fontWeight: 700,
            }}
          >
            {deviceCampaignLabel}
          </Box>
          <Stack
            direction="row"
            spacing={1.5}
            sx={{
              alignItems: 'baseline',
              flexWrap: 'wrap',
            }}
          >
            <Typography sx={{ color: 'text.secondary', textDecoration: 'line-through', fontSize: 16 }}>定価 {devicePriceNumber}円</Typography>
            <Typography sx={{ fontWeight: 900, fontSize: 40, color: 'primary.dark', lineHeight: 1 }}>{deviceCampaignPriceNumber}円</Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              (税込)
            </Typography>
          </Stack>
          <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mt: 0.75, lineHeight: 1.55 }}>
            デバイス本体価格。管理Webアプリは無料 / 毎月の通信費0円 (約10年分を本体価格に含む)。
            {supportEmail && (
              <>
                <br />
                お問い合わせ:{' '}
                <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  {supportEmail}
                </Box>
              </>
            )}
          </Typography>
        </Box>

        {/* QR */}
        <Stack
          spacing={0.75}
          sx={{
            alignItems: 'center',
            flexShrink: 0,
          }}
        >
          <Box className="flyer-qr" sx={{ p: 1.5, backgroundColor: '#fff', borderRadius: 2, border: '1px solid', borderColor: 'divider', lineHeight: 0 }}>
            <QRCodeSVG value={baseURL} size={88} />
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Typography sx={{ fontSize: 12.5, fontWeight: 700 }}>スマホで詳しく</Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {baseURL}
            </Typography>
          </Box>
        </Stack>
      </Stack>
    </Paper>
  )
}
